import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { customersService, Customer } from '../services/customersService';
import {
  Card, Button, Typography, Spin, Alert, Tag, Divider, Tabs, Row, Col, Space, Descriptions, Statistic, Table, Empty
} from 'antd';
import {
  EditOutlined, ArrowLeftOutlined, MailOutlined, PhoneOutlined, ShopOutlined, ToolOutlined,
  SolutionOutlined, AppstoreOutlined, EnvironmentOutlined, PlusOutlined, UserOutlined, EyeOutlined, CalendarOutlined
} from '@ant-design/icons';
import api from '../services/api';

const { Title, Text } = Typography;

const CustomerDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Données associées au client
  const [interventions, setInterventions] = useState<any[]>([]);
  const [loadingInterventions, setLoadingInterventions] = useState<boolean>(false);
  const [subscriptions, setSubscriptions] = useState<any[]>([]);
  const [equipments, setEquipments] = useState<any[]>([]);

  useEffect(() => {
    if (!id) return;
    const numId = Number(id);
    setLoading(true);

    customersService.getCustomerById(numId)
      .then(async (custData) => {
        setCustomer(custData);
        const userId = custData.user_id || custData.user?.id;

        const extractArray = (resData: any, ...keys: string[]): any[] => {
          if (!resData) return [];
          if (Array.isArray(resData)) return resData;
          for (const k of keys) {
            if (Array.isArray(resData[k])) return resData[k];
          }
          if (resData.data) {
            if (Array.isArray(resData.data)) return resData.data;
            for (const k of keys) {
              if (Array.isArray(resData.data[k])) return resData.data[k];
            }
          }
          return [];
        };

        // 1. Charger les interventions du client
        setLoadingInterventions(true);
        try {
          const res = await api.get('/interventions', {
            params: {
              limit: 1000,
              include_unpaid: 'true',
              customer_id: numId
            }
          });
          const allList = extractArray(res.data, 'interventions');
          const filtered = allList.filter((item: any) => 
            Number(item.customer_id) === numId || 
            (userId && Number(item.customer_id) === Number(userId)) ||
            Number(item.customerId) === numId ||
            (userId && Number(item.customerId) === Number(userId)) ||
            Number(item.customer?.id) === numId ||
            (userId && Number(item.customer?.id) === Number(userId)) ||
            (userId && Number(item.customer?.user_id) === Number(userId)) ||
            (userId && Number(item.customer?.user?.id) === Number(userId))
          );
          setInterventions(filtered);
        } catch (e) {
          console.warn('Erreur chargement interventions:', e);
        } finally {
          setLoadingInterventions(false);
        }

        // 2. Charger les abonnements
        try {
          const resSub = await api.get('/admin/subscriptions', {
            params: {
              includePending: 'true',
              customer_id: numId
            }
          });
          const subs = extractArray(resSub.data, 'subscriptions');
          const filteredSubs = subs.filter((s: any) => 
            Number(s.customer_id) === numId || 
            (userId && Number(s.customer_id) === Number(userId)) ||
            Number(s.customer?.id) === numId ||
            (userId && Number(s.customer?.id) === Number(userId)) ||
            (userId && Number(s.customer?.user_id) === Number(userId)) ||
            (userId && Number(s.customer?.user?.id) === Number(userId))
          );
          setSubscriptions(filteredSubs);
        } catch (e) {
          console.warn('Erreur chargement abonnements:', e);
        }

        // 3. Charger les équipements (splits)
        try {
          const resEq = await api.get('/splits', {
            params: {
              limit: 1000,
              customerId: numId
            }
          });
          const eqList = extractArray(resEq.data, 'splits');
          const filteredEq = eqList.filter((e: any) => 
            Number(e.customer_id) === numId || 
            (userId && Number(e.customer_id) === Number(userId)) ||
            Number(e.userId) === numId ||
            (userId && Number(e.userId) === Number(userId)) ||
            Number(e.customer?.id) === numId ||
            (userId && Number(e.customer?.user_id) === Number(userId))
          );
          setEquipments(filteredEq);
        } catch (e) {
          console.warn('Erreur chargement équipements:', e);
        }
      })
      .catch(() => setError('Erreur lors du chargement du client'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div style={{ padding: 60, textAlign: 'center' }}><Spin size="large" tip="Chargement du profil client..." /></div>;
  if (error || !customer) return <Alert message={error || 'Client non trouvé'} type="error" style={{ margin: 16 }} />;

  const statusLabel: Record<string, string> = {
    pending: 'En attente',
    assigned: 'Assignée',
    accepted: 'Acceptée',
    on_the_way: 'En route',
    arrived: 'Sur place',
    in_progress: 'En cours',
    diagnostic_submitted: 'Diagnostic soumis',
    execution_confirmed: 'Travaux confirmés',
    completed: 'Terminée',
    cancelled: 'Annulée',
    scheduled: 'Programmée',
    active: 'Actif',
    inactive: 'Inactif',
    suspended: 'Suspendu'
  };

  const statusColor: Record<string, string> = {
    pending: 'orange',
    assigned: 'purple',
    accepted: 'cyan',
    on_the_way: 'blue',
    arrived: 'geekblue',
    in_progress: 'processing',
    diagnostic_submitted: 'cyan',
    execution_confirmed: 'green',
    completed: 'success',
    cancelled: 'error',
    scheduled: 'blue',
    active: 'success',
    inactive: 'default',
    suspended: 'error'
  };

  const companyTypeLabel: Record<string, string> = {
    particulier: 'Particulier',
    sante: 'Santé',
    commerce: 'Commerce',
    entreprise: 'Entreprise',
    administration: 'Administration',
    household: 'Particulier',
    healthcare: 'Santé',
    enterprise: 'Entreprise',
  };

  const fullName = `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || 'Client';

  const interventionColumns = [
    {
      title: 'Titre / Référence',
      dataIndex: 'title',
      key: 'title',
      render: (text: string, record: any) => (
        <div>
          <Text strong style={{ color: '#0f172a' }}>{text || `Intervention #${record.id}`}</Text>
          {record.intervention_type && (
            <div style={{ fontSize: 12, color: '#64748b' }}>
              Type: {record.intervention_type.toUpperCase()}
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Date prévue',
      dataIndex: 'scheduled_date',
      key: 'scheduled_date',
      render: (date: string) => date ? new Date(date).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '-'
    },
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      render: (st: string) => (
        <Tag color={statusColor[st] || 'default'} style={{ borderRadius: 12, padding: '2px 10px', fontWeight: 600 }}>
          {statusLabel[st] || st}
        </Tag>
      )
    },
    {
      title: 'Action',
      key: 'action',
      render: (_: any, record: any) => (
        <Button
          size="small"
          type="primary"
          ghost
          icon={<EyeOutlined />}
          onClick={() => navigate(`/interventions?id=${record.id}`)}
        >
          Consulter
        </Button>
      )
    }
  ];

  // Colonnes pour les souscriptions
  const subscriptionColumns = [
    {
      title: 'Offre / Formule',
      dataIndex: 'offer_snapshot',
      key: 'offer',
      render: (snapshot: any, record: any) => (
        <div>
          <Text strong>{snapshot?.title || record.offer?.title || `Contrat #${record.id}`}</Text>
          <div style={{ fontSize: 12, color: '#64748b' }}>
            {record.equipment_count || 1} équipement(s)
          </div>
        </div>
      )
    },
    {
      title: 'Visites',
      key: 'visits',
      render: (_: any, record: any) => (
        <Tag color="blue" style={{ fontWeight: 600 }}>
          {record.visits_completed || 0} / {record.visits_total || 4} visites
        </Tag>
      )
    },
    {
      title: 'Paiement',
      dataIndex: 'payment_status',
      key: 'payment_status',
      render: (st: string) => (
        <Tag color={st === 'paid' ? 'green' : st === 'partial' ? 'orange' : 'volcano'}>
          {st === 'paid' ? 'Payé (100%)' : st === 'partial' ? 'Acompte 50%' : 'En attente'}
        </Tag>
      )
    },
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      render: (st: string) => (
        <Tag color={st === 'active' ? 'success' : st === 'used' ? 'purple' : 'default'}>
          {st === 'active' ? 'Actif' : st === 'used' ? 'Terminé' : st}
        </Tag>
      )
    }
  ];

  return (
    <div style={{ padding: '16px 24px 40px 24px' }}>
      {/* Navigation supérieure */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <Space size="middle">
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/clients')} style={{ borderRadius: 8 }}>
            Retour
          </Button>
          <Title level={4} style={{ margin: 0, fontWeight: 700, color: '#0f172a' }}>
            Fiche Client #{customer.id}
          </Title>
        </Space>
        
        <Space size="small">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            style={{ backgroundColor: '#0a543d', borderColor: '#0a543d', fontWeight: 600, borderRadius: 8 }}
            onClick={() => navigate('/interventions')}
          >
            Nouvelle Intervention
          </Button>
          <Button
            icon={<EditOutlined />}
            style={{ borderRadius: 8 }}
            onClick={() => navigate(`/customers/edit/${customer.id}`)}
          >
            Modifier
          </Button>
        </Space>
      </div>

      {/* HERO BANNER - CARTE DE PROFIL CLIENT */}
      <Card
        bordered={false}
        style={{
          background: 'linear-gradient(135deg, #0a543d 0%, #11694e 60%, #063828 100%)',
          borderRadius: 16,
          boxShadow: '0 10px 25px rgba(10, 84, 61, 0.22)',
          marginBottom: 24,
          position: 'relative',
          overflow: 'hidden'
        }}
        bodyStyle={{ padding: '28px 32px' }}
      >
        <ToolOutlined
          style={{
            position: 'absolute', right: -20, bottom: -30, fontSize: 220, color: 'rgba(255, 255, 255, 0.04)', pointerEvents: 'none'
          }}
        />

        <Row gutter={[24, 24]} align="middle">
          <Col xs={24} md={18}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
              <div style={{
                width: 76, height: 76, borderRadius: '50%', background: '#EEBD1B', color: '#0F172A',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, fontWeight: 700,
                border: '3px solid #ffffff', boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
              }}>
                {(customer.first_name?.[0] || 'C') + (customer.last_name?.[0] || '')}
              </div>

              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6, flexWrap: 'wrap' }}>
                  <h1 style={{ color: '#ffffff', margin: 0, fontSize: 26, fontWeight: 700 }}>{fullName}</h1>
                  <Tag color="gold" style={{ fontSize: 12, fontWeight: 700, padding: '2px 10px', borderRadius: 12, border: 'none' }}>
                    {companyTypeLabel[customer.company_type || ''] || customer.company_type || 'Client'}
                  </Tag>
                  <Tag color={customer.user?.status === 'active' ? 'success' : 'warning'} style={{ fontSize: 12, fontWeight: 700, padding: '2px 10px', borderRadius: 12 }}>
                    {statusLabel[customer.user?.status || 'active'] || 'Actif'}
                  </Tag>
                </div>

                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 10 }}>
                  {customer.user?.email && (
                    <span style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', padding: '4px 14px', borderRadius: 20, fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <MailOutlined style={{ color: '#EEBD1B' }} /> {customer.user.email}
                    </span>
                  )}
                  {customer.user?.phone && (
                    <span style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', padding: '4px 14px', borderRadius: 20, fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <PhoneOutlined style={{ color: '#4ade80' }} /> {customer.user.phone}
                    </span>
                  )}
                  {(customer.city || customer.commune) && (
                    <span style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', padding: '4px 14px', borderRadius: 20, fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <EnvironmentOutlined style={{ color: '#60a5fa' }} /> {[customer.commune, customer.city].filter(Boolean).join(', ')}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </Card>

      {/* BARRE KPI STATISTIQUES */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={6}>
          <Card bordered={false} style={{ background: '#FFF7ED', border: '1px solid #FFEDD5', borderRadius: 12 }} bodyStyle={{ padding: 18 }}>
            <Statistic
              title={<Text style={{ color: '#C2410C', fontWeight: 600, fontSize: 13 }}>Interventions Total</Text>}
              value={interventions.length}
              prefix={<ToolOutlined style={{ color: '#EA580C', marginRight: 8 }} />}
              valueStyle={{ color: '#9A3412', fontWeight: 700, fontSize: 24 }}
            />
          </Card>
        </Col>

        <Col xs={12} sm={6}>
          <Card bordered={false} style={{ background: '#F0FDF4', border: '1px solid #DCFCE7', borderRadius: 12 }} bodyStyle={{ padding: 18 }}>
            <Statistic
              title={<Text style={{ color: '#15803D', fontWeight: 600, fontSize: 13 }}>Contrats & Abonnements</Text>}
              value={subscriptions.length}
              prefix={<SolutionOutlined style={{ color: '#059669', marginRight: 8 }} />}
              valueStyle={{ color: '#166534', fontWeight: 700, fontSize: 24 }}
            />
          </Card>
        </Col>

        <Col xs={12} sm={6}>
          <Card bordered={false} style={{ background: '#EFF6FF', border: '1px solid #DBEAFE', borderRadius: 12 }} bodyStyle={{ padding: 18 }}>
            <Statistic
              title={<Text style={{ color: '#1D4ED8', fontWeight: 600, fontSize: 13 }}>Climatiseurs & Splits</Text>}
              value={equipments.length}
              prefix={<AppstoreOutlined style={{ color: '#2563EB', marginRight: 8 }} />}
              valueStyle={{ color: '#1E40AF', fontWeight: 700, fontSize: 24 }}
            />
          </Card>
        </Col>

        <Col xs={12} sm={6}>
          <Card bordered={false} style={{ background: '#FAF5FF', border: '1px solid #F3E8FF', borderRadius: 12 }} bodyStyle={{ padding: 18 }}>
            <Statistic
              title={<Text style={{ color: '#6D28D9', fontWeight: 600, fontSize: 13 }}>Entreprise</Text>}
              value={customer.company_name || 'Particulier'}
              prefix={<ShopOutlined style={{ color: '#7C3AED', marginRight: 8 }} />}
              valueStyle={{ color: '#5B21B6', fontWeight: 700, fontSize: 18 }}
            />
          </Card>
        </Col>
      </Row>

      {/* CONTENU DES ONGLETS */}
      <Card bordered={false} style={{ borderRadius: 16 }} bodyStyle={{ padding: '20px 24px' }}>
        <Tabs
          defaultActiveKey="1"
          size="large"
          items={[
            {
              key: '1',
              label: <span style={{ fontWeight: 600, fontSize: 15 }}><UserOutlined /> Identité & Localisation</span>,
              children: (
                <div style={{ paddingTop: 16 }}>
                  <Row gutter={[24, 24]}>
                    <Col xs={24} md={12}>
                      <Card title="Identité & Contact" size="small" style={{ borderRadius: 12 }}>
                        <Descriptions column={1} size="middle" bordered>
                          <Descriptions.Item label="Nom complet">{fullName}</Descriptions.Item>
                          <Descriptions.Item label="Email">{customer.user?.email || '-'}</Descriptions.Item>
                          <Descriptions.Item label="Téléphone">{customer.user?.phone || '-'}</Descriptions.Item>
                          <Descriptions.Item label="Entreprise">{customer.company_name || '-'}</Descriptions.Item>
                          <Descriptions.Item label="Type d'entreprise">{companyTypeLabel[customer.company_type || ''] || customer.company_type || '-'}</Descriptions.Item>
                        </Descriptions>
                      </Card>
                    </Col>
                    <Col xs={24} md={12}>
                      <Card title="Localisation" size="small" style={{ borderRadius: 12 }}>
                        <Descriptions column={1} size="middle" bordered>
                          <Descriptions.Item label="Pays">{customer.country || 'Côte d\'Ivoire'}</Descriptions.Item>
                          <Descriptions.Item label="Ville">{customer.city || '-'}</Descriptions.Item>
                          <Descriptions.Item label="Commune">{customer.commune || '-'}</Descriptions.Item>
                          <Descriptions.Item label="GPS">{customer.latitude && customer.longitude ? `${customer.latitude}, ${customer.longitude}` : '-'}</Descriptions.Item>
                        </Descriptions>
                      </Card>
                    </Col>
                  </Row>
                </div>
              )
            },
            {
              key: '2',
              label: <span style={{ fontWeight: 600, fontSize: 15 }}><ToolOutlined /> Interventions ({interventions.length})</span>,
              children: (
                <div style={{ paddingTop: 16 }}>
                  {loadingInterventions ? (
                    <div style={{ padding: 40, textAlign: 'center' }}><Spin /></div>
                  ) : interventions.length === 0 ? (
                    <Empty description="Aucune intervention enregistrée pour ce client" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                  ) : (
                    <Table dataSource={interventions} columns={interventionColumns} rowKey="id" pagination={{ pageSize: 5 }} />
                  )}
                </div>
              )
            },
            {
              key: '3',
              label: <span style={{ fontWeight: 600, fontSize: 15 }}><SolutionOutlined /> Abonnements & Contrats ({subscriptions.length})</span>,
              children: (
                <div style={{ paddingTop: 16 }}>
                  {subscriptions.length === 0 ? (
                    <Empty description="Aucun abonnement enregistré pour ce client" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                  ) : (
                    <Table dataSource={subscriptions} columns={subscriptionColumns} rowKey="id" pagination={{ pageSize: 5 }} />
                  )}
                </div>
              )
            },
            {
              key: '4',
              label: <span style={{ fontWeight: 600, fontSize: 15 }}><AppstoreOutlined /> Équipements Climatisation ({equipments.length})</span>,
              children: (
                <div style={{ paddingTop: 16 }}>
                  {equipments.length === 0 ? (
                    <Empty description="Aucun équipement enregistré" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                  ) : (
                    <Row gutter={[16, 16]}>
                      {equipments.map((eq: any) => (
                        <Col xs={24} sm={12} md={8} key={eq.id}>
                          <Card size="small" style={{ borderRadius: 12 }}>
                            <Text strong>{eq.brand || 'Climatiseur'} {eq.model || ''}</Text>
                            <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                              Emplacement: {eq.location || 'Salon'}
                            </div>
                          </Card>
                        </Col>
                      ))}
                    </Row>
                  )}
                </div>
              )
            }
          ]}
        />
      </Card>
    </div>
  );
};

export default CustomerDetail;

